import { useSubscription } from '../contexts/SubscriptionContext';
import {
  DEFAULT_PLAN_CONFIG,
  SUBSCRIPTION_FEATURE_KEYS,
  DASHBOARD_MODES,
} from '../consts/SubscriptionFeatures';

/**
 * Hook customizado para verificar features, limites e permissões do plano de assinatura.
 *
 * Lê `planConfig` diretamente do SubscriptionContext (campo separado retornado pela API
 * como `plan_config` no endpoint /subscription/company/get.php).
 * Quando não há configuração disponível, usa DEFAULT_PLAN_CONFIG como fallback
 * permissivo (nunca bloqueia por erro).
 *
 * ATENÇÃO: NÃO usar subscription?.plan_config — o plan_config é um campo separado
 * no contexto, não está dentro do objeto subscription.
 *
 * @returns {Object} Métodos e dados de verificação de features
 */
const useSubscriptionFeatures = () => {
  const { subscription, planConfig: contextPlanConfig } = useSubscription();

  // Usa planConfig do contexto (campo separado retornado pela API como plan_config)
  // Fallback para DEFAULT_PLAN_CONFIG se não houver config (nunca bloquear por erro)
  const planConfig = contextPlanConfig ?? DEFAULT_PLAN_CONFIG;

  /**
   * Verifica se o plano tem uma permissão específica habilitada.
   * @param {string} permissionKey - Chave da permissão (use SUBSCRIPTION_PERMISSIONS)
   * @returns {boolean} True se a permissão está habilitada (fallback: true)
   */
  const hasPermission = (permissionKey) => {
    const permissions = planConfig?.permissions ?? DEFAULT_PLAN_CONFIG.permissions;
    const value = permissions?.[permissionKey];
    // Se a chave não existir no config, fallback permissivo
    return value !== undefined ? Boolean(value) : true;
  };

  /**
   * Retorna o limite numérico para um recurso específico.
   * @param {string} limitKey - Chave do limite (use SUBSCRIPTION_LIMITS)
   * @returns {number|null} Limite numérico ou null (sem limite)
   */
  const getLimit = (limitKey) => {
    const limits = planConfig?.limits ?? DEFAULT_PLAN_CONFIG.limits;
    const value = limits?.[limitKey];
    // Se a chave não existir no config, fallback sem limite
    return value !== undefined ? value : null;
  };

  /**
   * Verifica se a contagem atual está dentro do limite do plano.
   * @param {string} limitKey - Chave do limite (use SUBSCRIPTION_LIMITS)
   * @param {number} currentCount - Quantidade atual em uso
   * @returns {boolean} True se está dentro do limite (null = sempre true)
   */
  const isWithinLimit = (limitKey, currentCount) => {
    const limit = getLimit(limitKey);
    if (limit === null) return true; // sem limite
    return currentCount < limit;
  };

  /**
   * Retorna o valor de uma feature específica do plano.
   * @param {string} featureKey - Chave da feature (use SUBSCRIPTION_FEATURE_KEYS)
   * @returns {*} Valor da feature ou undefined se não configurada
   */
  const getFeatureValue = (featureKey) => {
    const features = planConfig?.features ?? DEFAULT_PLAN_CONFIG.features;
    return features?.[featureKey];
  };

  /**
   * Atalho: verifica se o dashboard está no modo avançado.
   * @returns {boolean} True se o dashboard é avançado
   */
  const isDashboardAdvanced = () => {
    const value = getFeatureValue(SUBSCRIPTION_FEATURE_KEYS.DASHBOARD);
    return value === DASHBOARD_MODES.ADVANCED;
  };

  return {
    planConfig,
    hasPermission,
    getLimit,
    isWithinLimit,
    getFeatureValue,
    isDashboardAdvanced,
  };
};

export default useSubscriptionFeatures;
